import Microgame from '../engine/Microgame.js';

export default class BalanceGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Équilibre !`, en: `Balance!`, es: `¡Equilibrio!` },
            controls: { fr: `Incline ton téléphone ou clique à gauche/droite`, en: `Tilt your device or click left/right`, es: `Inclina tu dispositivo o haz clic a los lados` },
            objective: { fr: `Maintiens la balle en équilibre au centre jusqu'à la fin`, en: `Keep the ball balanced in the center until time ends`, es: `Mantén la bola equilibrada en el centro` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M2 14h20M12 14v8M8 22h8"/><circle cx="12" cy="10" r="4"/></svg>`,
            actions: [
                { type: 'move', x: 50, y: 75, duration: 200 },
                { type: 'move', x: 35, y: 75, duration: 600 },
                { type: 'move', x: 55, y: 75, duration: 500 },
                { type: 'move', x: 45, y: 75, duration: 400 },
                { type: 'move', x: 60, y: 75, duration: 500 },
                { type: 'move', x: 48, y: 75, duration: 400 },
                { type: 'move', x: 52, y: 75, duration: 300 },
            ],
            loopDuration: 3500
        };
    }
    constructor(engine) {
        super(engine);
        this.ballPos = 0; // -100 to 100
        this.ballVel = 0;
        this.beamAngle = 0; // -20 to 20 degrees
        this.sliderVal = 50; // 0 to 100
        this.elapsedTime = 0;

        // Longer duration so the balance challenge is meaningful even at high speed
        // At x1: 5000/1 = 5s, at x2: 2.5s, at x3: 1.67s, at x4: 1.25s
        this.duration = 5000;
    }

    setup() {
        if (this.engine.activeChallenge && this.engine.activeChallenge.mode === 'infinite') {
            this.hideTimer = true;
        }
        this.engine.ui.instruction.textContent = "BALANCE!";

        const sm = this.engine.speedMultiplier || 1;

        // Beam width shrinks slightly with difficulty
        const width = Math.max(150, 300 - (sm - 1) * 30 + (Math.random() - 0.5) * 50);
        this.halfWidth = width / 2;

        // Gravity increases with speed multiplier → ball reacts faster to tilt
        // x1: 500, x2: 650, x3: 800, x4: 950
        this.gravityStrength = 500 + (sm - 1) * 150;

        // Beam
        this.beam = document.createElement('div');
        this.beam.style.position = 'absolute';
        this.beam.style.top = '40%';
        this.beam.style.left = '50%';
        this.beam.style.width = `${width}px`;
        this.beam.style.height = '10px';
        this.beam.style.background = this.primaryColor;
        this.beam.style.transform = 'translateX(-50%)';
        this.beam.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.container.appendChild(this.beam);

        // Initial tilt gets steeper with speed → ball moves immediately
        // x1: gentle random tilt (30-70), x4: extreme tilt (15-35 or 65-85)
        const tiltRange = Math.min(40, 20 + (sm - 1) * 8); // x1: 20, x4: 44→capped 40
        const tiltDirection = Math.random() < 0.5 ? -1 : 1;
        const tiltOffset = (tiltRange * 0.5) + Math.random() * (tiltRange * 0.5);
        this.sliderVal = 50 + tiltDirection * tiltOffset;
        this.sliderVal = Math.max(5, Math.min(95, this.sliderVal));

        this.beamAngle = (this.sliderVal - 50) * 0.8;
        this.beam.style.transform = `translateX(-50%) rotate(${this.beamAngle}deg)`;

        // Total planned balls based on speed multiplier
        // x1: 1 ball, x2: 2 balls, x3+: 3 balls
        this.totalBalls = sm < 1.5 ? 1 : (sm < 2.5 ? 2 : 3);
        this.spawnedBalls = 1; // Start with 1

        // Game duration for spawn scheduling
        this.gameDuration = this.duration / sm; // effective ms

        // Start with 1 ball near center
        this.balls = [];
        const startPos = (Math.random() - 0.5) * 2 * (this.halfWidth * 0.3); // close to center
        const maxVel = 20 + (sm - 1) * 15;
        const startVel = (Math.random() - 0.5) * 2 * maxVel;

        const firstBall = document.createElement('div');
        firstBall.style.position = 'absolute';
        firstBall.style.top = '-30px';
        firstBall.style.left = `calc(50% + ${startPos}px)`;
        firstBall.style.width = '30px';
        firstBall.style.height = '30px';
        firstBall.style.background = this.secondaryColor;
        firstBall.style.borderRadius = '50%';
        firstBall.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        firstBall.style.transform = 'translateX(-50%)';
        this.beam.appendChild(firstBall);

        this.balls.push({ pos: startPos, vel: startVel, el: firstBall });

        // Keep reference to first ball for legacy compatibility
        this.ball = firstBall;
        this.ballPos = startPos;
        this.ballVel = startVel;

        // Slider Control
        const sliderContainer = document.createElement('div');
        sliderContainer.style.position = 'absolute';
        sliderContainer.style.bottom = '20%';
        sliderContainer.style.left = '50%';
        sliderContainer.style.transform = 'translateX(-50%)';
        sliderContainer.style.width = '80%';
        sliderContainer.style.height = '40px';
        sliderContainer.style.background = this.secondaryColor;
        sliderContainer.style.opacity = '0.3';
        sliderContainer.style.borderRadius = '20px';
        this.container.appendChild(sliderContainer);

        this.handle = document.createElement('div');
        this.handle.style.position = 'absolute';
        this.handle.style.top = '0';
        this.handle.style.left = `${this.sliderVal}%`;
        this.handle.style.width = '40px';
        this.handle.style.height = '40px';
        this.handle.style.background = this.primaryColor;
        this.handle.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.handle.style.borderRadius = '50%';
        this.handle.style.transform = 'translateX(-50%)';
        this.handle.style.cursor = 'grab';
        sliderContainer.appendChild(this.handle);

        // Input
        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('touchmove', this.onMove);
    }

    onMove(e) {
        if (!this.active) return;
        let clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const rect = this.container.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

        this.sliderVal = x * 100;
        this.handle.style.left = `${this.sliderVal}%`;

        // Map slider to angle (-20 to 20)
        this.beamAngle = (this.sliderVal - 50) * 0.8; // Max 40 deg tilt
        this.beam.style.transform = `translateX(-50%) rotate(${this.beamAngle}deg)`;
    }

    // Survive until timeout = win!
    onTimeout() {
        return true;
    }

    update(dt) {
        this.elapsedTime += dt;
        // Physics
        const dtSec = dt / 1000;
        const gravityForce = Math.sin(this.beamAngle * Math.PI / 180) * this.gravityStrength;

        // Progressive ball spawning in STANDARD mode
        // Ball 2 at ~35% of game time, Ball 3 at ~65%
        if (this.spawnedBalls < this.totalBalls) {
            const progress = this.elapsedTime / this.gameDuration;
            const nextSpawnAt = this.spawnedBalls === 1 ? 0.35 : 0.65;

            if (progress >= nextSpawnAt) {
                this.spawnedBalls++;

                // Each ball spawns further from center
                // Ball 2: ~50% of halfWidth, Ball 3: ~75% of halfWidth
                const spreadFactor = this.spawnedBalls === 2 ? 0.5 : 0.75;
                const range = this.halfWidth * spreadFactor;
                const side = Math.random() < 0.5 ? -1 : 1;
                const spawnPos = side * (range * 0.5 + Math.random() * range * 0.5);

                const ball = document.createElement('div');
                ball.style.position = 'absolute';
                ball.style.top = '-30px';
                ball.style.left = `calc(50% + ${spawnPos}px)`;
                ball.style.width = '30px';
                ball.style.height = '30px';
                ball.style.background = this.secondaryColor;
                ball.style.borderRadius = '50%';
                ball.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
                ball.style.transform = 'translateX(-50%)';
                this.beam.appendChild(ball);

                this.balls.push({ pos: spawnPos, vel: 0, el: ball });

                // Pop animation
                ball.animate([
                    { transform: 'translate(-50%, -50px) scale(0)' },
                    { transform: 'translate(-50%, 0) scale(1)' }
                ], { duration: 300 });
            }
        }

        // Update all balls
        for (let i = 0; i < this.balls.length; i++) {
            const b = this.balls[i];
            b.vel += gravityForce * dtSec;
            b.pos += b.vel * dtSec;

            // Render
            b.el.style.left = `calc(50% + ${b.pos}px)`;

            // Lose condition (grace period for new balls: 0.5s from game start)
            if (this.elapsedTime > 500) {
                if (Math.abs(b.pos) > (this.halfWidth || 150)) {
                    this.lose();
                    return;
                }
            }
        }

        // Win / Infinite Progress
        if (this.engine.activeChallenge && this.engine.activeChallenge.mode === 'infinite') {
            this.score = (this.score || 0) + dtSec;
            const currentScore = Math.floor(this.score);
            this.engine.score = currentScore;
            this.engine.updateHUD();

            // Spawn extra ball every 10 points
            if (!this.lastSpawnScore) this.lastSpawnScore = 0;

            if (currentScore >= 10 && currentScore % 10 === 0 && currentScore > this.lastSpawnScore) {
                this.lastSpawnScore = currentScore;
                this.spawnExtraBall();
            }
        }
    }

    spawnExtraBall() {
        const ball = document.createElement('div');
        ball.style.position = 'absolute';
        ball.style.top = '-30px';
        ball.style.left = '50%';
        ball.style.width = '30px';
        ball.style.height = '30px';
        ball.style.background = this.secondaryColor;
        ball.style.borderRadius = '50%';
        ball.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        ball.style.transform = 'translateX(-50%)';
        this.beam.appendChild(ball);

        const range = (this.halfWidth || 150) * 0.6;
        const startPos = (Math.random() - 0.5) * 2 * range;

        this.balls.push({
            pos: startPos,
            vel: 0,
            el: ball
        });

        // Visual pop
        ball.animate([
            { transform: 'translate(-50%, -50px) scale(0)' },
            { transform: 'translate(-50%, 0) scale(1)' }
        ], { duration: 300 });
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('touchmove', this.onMove);
    }
}
